<script setup>
import { ref, computed, watch, onUnmounted } from 'vue';
import BaseBubble from './Base.vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import { ATTACHMENT_TYPES } from '../constants';

const emit = defineEmits(['error']);
const hasError = ref(false);
const showGallery = ref(false);
const isLoading = ref(true);
const { filteredCurrentChatAttachments, attachments } = useMessageContext();

const attachment = computed(() => {
  return attachments.value[0];
});

// Cache global de vídeos já carregados com sucesso
const loadedVideoCache = new Set();

const displayUrl = ref(null);
const retryCount = ref(0);
const maxRetries = 5;
let retryTimeoutId = null;

// Gera uma chave de cache baseada no attachment ID
const getCacheKey = url => {
  const attachmentId = attachment.value?.id;
  if (attachmentId) return `video-${attachmentId}`;
  if (url) return `video-url-${url.split('/').pop()?.substring(0, 30)}`;
  return null;
};

// Função para testar se o vídeo pode ser carregado
const testVideoLoad = url => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';

    const cleanup = () => {
      video.onloadedmetadata = null;
      video.onerror = null;
      video.src = '';
    };

    video.onloadedmetadata = () => {
      cleanup();
      resolve(url);
    };

    video.onerror = () => {
      cleanup();
      reject(new Error('Video load failed'));
    };

    video.src = url;
  });
};

const loadVideo = async url => {
  if (!url) return;

  const cacheKey = getCacheKey(url);

  // Se já carregou com sucesso antes, usar direto
  if (cacheKey && loadedVideoCache.has(cacheKey)) {
    displayUrl.value = url;
    isLoading.value = false;
    hasError.value = false;
    return;
  }

  isLoading.value = true;
  hasError.value = false;

  try {
    await testVideoLoad(url);

    // Cache successful load
    if (cacheKey) {
      loadedVideoCache.add(cacheKey);
    }

    displayUrl.value = url;
    isLoading.value = false;
    hasError.value = false;
    retryCount.value = 0;
  } catch {
    if (retryCount.value < maxRetries) {
      retryCount.value += 1;
      const delay = 500 * retryCount.value;

      if (retryTimeoutId) {
        clearTimeout(retryTimeoutId);
      }

      retryTimeoutId = setTimeout(() => {
        const currentUrl = attachment.value?.dataUrl;
        if (currentUrl) {
          loadVideo(currentUrl);
        }
      }, delay);
    } else {
      isLoading.value = false;
      hasError.value = true;
      emit('error');
    }
  }
};

// Watch for URL changes
watch(
  () => attachment.value?.dataUrl,
  newUrl => {
    if (newUrl) {
      retryCount.value = 0;
      loadVideo(newUrl);
    }
  },
  { immediate: true }
);

onUnmounted(() => {
  if (retryTimeoutId) {
    clearTimeout(retryTimeoutId);
  }
});

const handleError = () => {
  // Se o vídeo visível der erro, tentar recarregar
  if (!isLoading.value && displayUrl.value) {
    retryCount.value = 0;
    loadVideo(attachment.value?.dataUrl);
  }
};

const isReel = computed(() => {
  return attachment.value.fileType === ATTACHMENT_TYPES.IG_REEL;
});
</script>

<template>
  <BaseBubble
    class="overflow-hidden p-3"
    data-bubble-name="video"
    @click="showGallery = true"
  >
    <div v-if="hasError" class="flex items-center gap-1 text-center rounded-lg">
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      <p class="mb-0 text-n-slate-11">
        {{ $t('COMPONENTS.MEDIA.VIDEO_UNAVAILABLE') }}
      </p>
    </div>
    <div v-else class="relative group rounded-lg overflow-hidden">
      <!-- Loading placeholder -->
      <div
        v-if="isLoading && !displayUrl"
        class="flex items-center justify-center bg-n-alpha-2 rounded-lg w-[300px] h-[200px]"
      >
        <Icon
          icon="i-lucide-loader-2"
          class="text-n-slate-11 animate-spin size-6"
        />
      </div>
      <div
        v-if="isReel"
        class="absolute p-2 flex items-start justify-end right-0 pointer-events-none"
      >
        <Icon icon="i-lucide-instagram" class="text-white shadow-lg" />
      </div>
      <video
        v-show="displayUrl"
        controls
        class="rounded-lg skip-context-menu"
        :src="displayUrl"
        :class="{
          'max-w-48': isReel,
          'max-w-full': !isReel,
        }"
        @error="handleError"
      />
    </div>
  </BaseBubble>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="onError"
    @close="() => (showGallery = false)"
  />
</template>
